import {
  CircleCheck,
  Info,
  LoaderCircle,
  OctagonAlert,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { ApiError } from '../api/client.ts';
import { details } from '../forms.ts';
import { useLang } from '../i18n.ts';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ── Buttons ─────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover border border-transparent shadow-sm',
  secondary: 'bg-surface text-text border border-border-input hover:bg-surface-2',
  ghost: 'bg-transparent text-text border border-transparent hover:bg-surface-2',
  // Destructive actions: outlined crimson with an icon, never a filled brand-red look-alike.
  danger: 'bg-surface text-error border-2 border-dashed border-error-border hover:bg-error-bg',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon: Icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variant?: ButtonVariant;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly loading?: boolean;
  readonly icon?: LucideIcon;
}) {
  const sizes = {
    sm: 'h-9 px-3 text-sm gap-1.5',
    md: 'h-11 px-4 text-sm gap-2',
    lg: 'h-12 px-5 text-base gap-2',
  };
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_STYLES[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <LoaderCircle className="size-4 animate-spin" aria-hidden />
      ) : Icon ? (
        <Icon className="size-4 shrink-0" aria-hidden />
      ) : null}
      {children}
    </button>
  );
}

// ── Form fields ─────────────────────────────────────────────────────────────

const CONTROL =
  'w-full rounded-lg border border-border-input bg-surface px-3 text-text placeholder:text-muted ' +
  'aria-[invalid=true]:border-2 aria-[invalid=true]:border-dashed aria-[invalid=true]:border-error-border';

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | null | undefined;
  readonly children: ReactNode;
  readonly htmlFor: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-text">
        {label}
      </label>
      {children}
      {/* While there is an error only the error shows, right under the field (owner feedback, D97). */}
      {error ? <FieldError id={`${htmlFor}-error`} message={error} /> : null}
      {hint && !error ? (
        <p id={`${htmlFor}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function FieldError({ id, message }: { readonly id: string; readonly message: string }) {
  const { t } = useLang();
  return (
    <p id={id} className="flex items-start gap-2 text-base font-bold text-error" role="alert">
      <OctagonAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
      <span>
        <span className="sr-only">{t('common.errorLabel')}: </span>
        {message}
      </span>
    </p>
  );
}

function describedBy(id: string, hint: boolean, error: boolean): string | undefined {
  const ids = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

export function TextInput({
  label,
  hint,
  error,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | null | undefined;
}) {
  const generated = useId();
  const id = rest.id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <input
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, Boolean(hint) && !error, Boolean(error))}
        className={cx(CONTROL, 'h-11', className)}
      />
    </Field>
  );
}

export function SelectInput({
  label,
  hint,
  error,
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | null | undefined;
}) {
  const generated = useId();
  const id = rest.id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <select
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, Boolean(hint) && !error, Boolean(error))}
        className={cx(CONTROL, 'h-11', className)}
      >
        {children}
      </select>
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  error,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | null | undefined;
}) {
  const generated = useId();
  const id = rest.id ?? generated;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
      <textarea
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, Boolean(hint) && !error, Boolean(error))}
        className={cx(CONTROL, 'min-h-24 py-2', className)}
      />
    </Field>
  );
}

// ── Alerts (never color alone: icon + label + border style) ─────────────────

type Tone = 'success' | 'info' | 'warning' | 'error';

const TONES: Record<
  Tone,
  {
    icon: LucideIcon;
    box: string;
    label: 'successLabel' | 'infoLabel' | 'warningLabel' | 'errorLabel';
  }
> = {
  success: {
    icon: CircleCheck,
    box: 'bg-success-bg text-success border border-success-border',
    label: 'successLabel',
  },
  info: { icon: Info, box: 'bg-info-bg text-info border border-info-border', label: 'infoLabel' },
  warning: {
    icon: TriangleAlert,
    box: 'bg-warning-bg text-warning border-2 border-warning-border',
    label: 'warningLabel',
  },
  error: {
    icon: OctagonAlert,
    box: 'bg-error-bg text-error border-2 border-dashed border-error-border',
    label: 'errorLabel',
  },
};

export function Alert({
  tone,
  title,
  children,
  onClose,
}: {
  readonly tone: Tone;
  readonly title?: string | undefined;
  readonly children?: ReactNode;
  readonly onClose?: () => void;
}) {
  const { t } = useLang();
  const style = TONES[tone];
  const Icon = style.icon;
  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      className={cx('flex items-start gap-3 rounded-lg p-3 text-sm', style.box)}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-bold">{title ?? t(`common.${style.label}`)}</p>
        {children ? <div className="mt-0.5 text-text">{children}</div> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-surface-2"
          aria-label={t('common.close')}
        >
          <X className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/** Shows an API error in the person's language, with the request id for support. */
export function ErrorAlert({
  error,
  shownUnderFields = [],
}: {
  readonly error: unknown;
  /** Field paths whose reasons are already shown under the fields (not repeated here). */
  readonly shownUnderFields?: readonly string[];
}) {
  const { t, lang } = useLang();
  if (!error) return null;
  let message: string = t('common.unexpectedError');
  let requestId: string | null = null;
  let reasons: string[] = [];
  if (error instanceof ApiError) {
    if (error.code === 'NETWORK') message = t('common.networkError');
    else if (error.serverMessage) message = error.serverMessage[lang];
    requestId = error.requestId;
    reasons = details(error)
      .filter((d) => !shownUnderFields.includes(d.path))
      .map((d) => d.message[lang]);
  }
  return (
    <Alert tone="error">
      <p>{message}</p>
      {reasons.length > 0 ? (
        <ul className="mt-1 list-disc ps-5">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
      {requestId ? (
        <p className="num mt-1 text-xs text-muted">{t('common.requestId', { id: requestId })}</p>
      ) : null}
    </Alert>
  );
}

// ── Layout pieces ───────────────────────────────────────────────────────────

export function Card({
  title,
  actions,
  children,
  className,
}: {
  readonly title?: string;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section className={cx('rounded-xl border border-border bg-surface shadow-sm', className)}>
      {title || actions ? (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          {title ? <h2 className="text-base font-bold">{title}</h2> : <span />}
          {actions}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

type BadgeTone = 'neutral' | Tone | 'brand';

export function Badge({
  tone = 'neutral',
  icon: Icon,
  children,
}: {
  readonly tone?: BadgeTone;
  readonly icon?: LucideIcon;
  readonly children: ReactNode;
}) {
  const styles: Record<BadgeTone, string> = {
    neutral: 'bg-surface-2 text-muted border-border',
    // Filled brand red with white text: clearly branding, never mistaken for an error (dashed).
    brand: 'bg-primary text-on-primary border-primary',
    success: 'bg-success-bg text-success border-success-border',
    info: 'bg-info-bg text-info border-info-border',
    warning: 'bg-warning-bg text-warning border-warning-border',
    error: 'bg-error-bg text-error border-error-border border-dashed',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
        styles[tone],
      )}
    >
      {Icon ? <Icon className="size-3.5" aria-hidden /> : null}
      {children}
    </span>
  );
}

export function Spinner({ label }: { readonly label?: string }) {
  const { t } = useLang();
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-muted" role="status">
      <LoaderCircle className="size-5 animate-spin" aria-hidden />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}

export function Table({ children }: { readonly children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  className,
}: {
  readonly children?: ReactNode;
  readonly className?: string;
}) {
  return (
    <th
      scope="col"
      className={cx(
        'border-b border-border bg-surface-2 px-4 py-2.5 text-xs font-bold text-muted',
        // Amount columns align to the end; everything else to the start.
        className?.includes('text-end') ? undefined : 'text-start',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
}: {
  readonly children?: ReactNode;
  readonly className?: string;
}) {
  return (
    <td className={cx('border-b border-border px-4 py-3 align-middle', className)}>{children}</td>
  );
}

// ── Dialog (native <dialog>: focus trap, Escape and backdrop for free) ──────

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** For forms with a table inside (entry lines). */
  readonly wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useLang();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={cx(
        'm-auto rounded-xl border border-border bg-surface p-0 text-text shadow-xl backdrop:bg-black/50',
        wide ? 'w-[min(60rem,calc(100vw-2rem))]' : 'w-[min(32rem,calc(100vw-2rem))]',
      )}
    >
      {open ? (
        <div className="flex max-h-[85dvh] flex-col">
          <header className="flex items-center justify-between gap-2 border-b border-border px-5 py-4">
            <h2 className="text-lg font-bold">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 hover:bg-surface-2"
              aria-label={t('common.close')}
            >
              <X className="size-5" aria-hidden />
            </button>
          </header>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer ? (
            <footer className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
